'use client';

import { Recipe } from '@fdc/__generated__/graphql';
import { useFeatureFlag } from '@fdc/components/feature-flag-provider/feature-flag-provider';
import { CustomEventType } from '@fdc/constants/custom-event.constants';
import { FH_EXPERIMENT_RECIPE_TILE_ENABLE_SAVE_BUTTON } from '@fdc/constants/feature-hub.constants';
import { NAVIGATION_CONSTANTS } from '@fdc/constants/navigation.constants';
import { useAppRouter } from '@fdc/hooks/useAppRouter';
import { trackAnalyticsEvent } from '@fdc/utils/analytics';
import { getDamImageUrl } from '@fdc/utils/get-dam-image-url';
import { useRecipeTileListViewTracking } from '@fdc/utils/recipe-tile-list-view-tracking/useRecipeTileListViewTracking';
import { useIntersectionObserver } from '@woolworthslimited/core-components-react/shared/hooks/useIntersectionObserver';
import FeatureRecipeTile from '@woolworthslimited/core-components-react/tiles/recipe/recipe';

import { PromotionEventModel } from '../../nz/analytics-event/analytics-models';

import { RecipeGridBase } from './recipe-grid-base';

type RecipeGridItemProps = {
  recipe: Partial<Recipe>;
  index: number;
  canSaveRecipe: boolean;
  saveClickHandler?: (recipeId: string) => Promise<boolean>;
  removeClickHandler?: (recipeId: string) => Promise<boolean>;
  onAnalyticsTrack?: (eventType: string, data: PromotionEventModel) => void;
  onVisible: (recipe: Partial<Recipe>, index: number) => void;
  onRecipeClick: (slug: string) => void;
};

const RecipeGridItem = ({
  recipe,
  index,
  canSaveRecipe,
  saveClickHandler,
  removeClickHandler,
  onAnalyticsTrack,
  onVisible,
  onRecipeClick,
}: RecipeGridItemProps) => {
  const tileRef = useIntersectionObserver<HTMLDivElement>(
    (entry) => {
      if (entry.isIntersecting) {
        onVisible(recipe, index);
      }
    },
    { threshold: 0.5 }
  );

  const recipeTileProps = {
    title: recipe.title,
    image: getDamImageUrl(recipe.thumbnailUrl, 690),
    preparation: {
      time: recipe.prepTime,
    },
    cook: {
      time: recipe.cookTime,
    },
    serve: {
      total: recipe.serves,
    },
    recipeId: recipe.recipeId,
    isSponsored: recipe.isSponsored,
    isSaved: recipe.isFavourited,
    url: `${NAVIGATION_CONSTANTS.RECIPE}/${recipe.slug}`,
    clickHandler: () => {
      if (onAnalyticsTrack) {
        onAnalyticsTrack(CustomEventType.RECIPE_TILE_CLICK_EVENT, {
          recipe: recipe,
          index: index + 1,
        });
      } else {
        trackAnalyticsEvent(CustomEventType.RECIPE_TILE_CLICK_EVENT, {
          heading: recipe.title || 'Recipe tile',
          cta: recipe.title,
          recipe: recipe,
          index: index + 1,
        });
      }

      onRecipeClick(recipe.slug || '');
    },
    ...(canSaveRecipe && {
      saveClickHandler: (recipeId: string) => {
        trackAnalyticsEvent(CustomEventType.RECIPE_SAVE_CLICK_EVENT, {
          heading: 'Save ' + recipe.title,
          recipe: recipe,
        });
        return saveClickHandler?.(recipeId) ?? Promise.resolve(false);
      },
      removeClickHandler: (recipeId: string) => {
        trackAnalyticsEvent(CustomEventType.RECIPE_REMOVE_CLICK_EVENT, {
          heading: 'Unsave ' + recipe.title,
          recipe: recipe,
        });
        return removeClickHandler?.(recipeId) ?? Promise.resolve(false);
      },
    }),
  };

  return (
    <div ref={tileRef}>
      <FeatureRecipeTile {...recipeTileProps} />
    </div>
  );
};

export const RecipeGrid = ({
  recipeList = [],
  saveClickHandler,
  removeClickHandler,
  onAnalyticsTrack,
  analyticsHeading,
  analyticsSectionId,
  analyticsOption,
}: {
  recipeList?: Partial<Recipe>[];
  saveClickHandler?: (recipeId: string) => Promise<boolean>;
  removeClickHandler?: (recipeId: string) => Promise<boolean>;
  onAnalyticsTrack?: (eventType: string, data: PromotionEventModel) => void;
  analyticsHeading?: string;
  analyticsSectionId?: string;
  analyticsOption?: string;
}) => {
  const router = useAppRouter();
  const { queueRecipeView } = useRecipeTileListViewTracking({
    recipeList,
    analyticsHeading,
    analyticsSectionId,
    analyticsOption,
    onAnalyticsTrack,
  });

  const showSaveButton = useFeatureFlag(
    FH_EXPERIMENT_RECIPE_TILE_ENABLE_SAVE_BUTTON,
    false
  );
  const canSaveRecipe = Boolean(
    showSaveButton && saveClickHandler && removeClickHandler
  );

  return (
    <RecipeGridBase>
      {recipeList.length > 0 &&
        recipeList.map((recipe, index) => {
          return (
            <RecipeGridItem
              key={index}
              recipe={recipe}
              index={index}
              canSaveRecipe={canSaveRecipe}
              saveClickHandler={saveClickHandler}
              removeClickHandler={removeClickHandler}
              onAnalyticsTrack={onAnalyticsTrack}
              onVisible={queueRecipeView}
              onRecipeClick={(slug) =>
                router.push(`${NAVIGATION_CONSTANTS.RECIPE}/${slug}`)
              }
            />
          );
        })}
    </RecipeGridBase>
  );
};
